import React from 'react'
import { 
  Card, 
  CardBody, 
  CardHeader, 
  CardFooter, 
  Alert,
} from 'reactstrap'
import { Link } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import PropTypes from 'prop-types'
import { 
  BackupsTable, 
  UnwindStatus, 
  RestoreStatus,
} from './UploadCSVComponents'

/**
 * View for managing backups related to uploading CSVs
 * from printed bubble sheets. Allows delete and restore
 * uploads.
 * @param {object} props 
 */
function UploadCSVBackups ({
  assessmentName,
  backupRestoring,
  backups,
  backupUnwinding,
  error,
  linkToUpload,
  loading,
  loadingComplete,
  processedStudents,
  restore,
  restoreError,
  restoreStatus,
  schoolName,
  status,
  statusClasses,
  statusGroupUAs,
  statusStudents,
  statusTeachers,
  statusUserAssessments,
  totalStudents,
  unwind,
  unwindErrors,
  unwindPhase,
  year,
}) {
  // Utility function converts object from firestore
  // into an array of objects with id included
  function convertToArrayWithIds (obj) {
    if (!obj) return []
    return Object.keys(obj).map(id => {
      return {
        ...obj[id],
        id,
      }
    })
  }

  // If loading isn't complete, display the loader or
  // error message.
  if (!loadingComplete || error) {
    return (
      <Card>
        <CardBody>
          <BeatLoader loading={loading} color={'#00C1AB'} />
          {error && 
            <Alert color='danger'>Error loading.</Alert>
          }
        </CardBody>
      </Card>
    )
  }

  const backs = convertToArrayWithIds(backups)
  const is_processing = !!(backupRestoring || backupUnwinding)

  return (
    <Card>
      <CardHeader>
        <h2>Upload Backups</h2>
        <h5>{schoolName}</h5>
        <h5>{`${assessmentName}, ${year}`}</h5>
      </CardHeader>
      <CardBody>
        <BackupsTable
          backups={backs}
          is_processing={is_processing}
          restore={restore}
          unwind={unwind}
        />
        <Alert className='mt-1' color='warning'>
          <b>Warning: </b>Unwinding a backup will delete all 
          created records, even if they have been associated with
          more data since the upload. It is recommended that you
          only unwind data you have recently uploaded as this can 
          have unintended consequences.  
        </Alert>
        <UnwindStatus
          backup={backupUnwinding ? backups[backupUnwinding] : null}
          status={status}
          statusClasses={statusClasses}
          statusGroupUAs={statusGroupUAs}
          statusStudents={statusStudents}
          statusTeachers={statusTeachers}
          statusUserAssessments={statusUserAssessments}
          unwindErrors={unwindErrors}
          unwindPhase={unwindPhase}
        />
        <RestoreStatus 
          backup={backupRestoring ? backups[backupRestoring] : null}
          processedStudents={processedStudents}
          restoreError={restoreError}
          restoreStatus={restoreStatus}
          totalStudents={totalStudents}
        />
        {restoreError && 
          <Alert color='danger'>
            Error during restore process.
          </Alert>
        }
      </CardBody>
      <CardFooter>
        <Link to={linkToUpload}>{'< Back to Upload'}</Link>
      </CardFooter>
    </Card>
  )
}

const { array, bool, func, number, object, string, } = PropTypes
UploadCSVBackups.propTypes = {
  assessmentName: string.isRequired,
  backupRestoring: string.isRequired,
  backups: object,
  backupUnwinding: string.isRequired,
  error: bool.isRequired,
  linkToUpload: string,
  loading: bool.isRequired,
  loadingComplete: bool.isRequired,
  processedStudents: number.isRequired,
  restore: func.isRequired,
  restoreError: bool.isRequired,
  restoreStatus: string.isRequired,
  schoolName: string.isRequired,
  status: string.isRequired,
  statusClasses: number.isRequired,
  statusGroupUAs: number.isRequired,
  statusStudents: number.isRequired,
  statusTeachers: number.isRequired,
  statusUserAssessments: number.isRequired,
  totalStudents: number.isRequired,
  unwind: func.isRequired,
  unwindErrors: array.isRequired,
  unwindPhase: string.isRequired,
  year: string.isRequired,
}

export default UploadCSVBackups


// WEBPACK FOOTER //
// ./src/components/Assessment/UploadCSVBackups.js